/**
 * The last step of every volunteer booking (Eli, 17 Oct 2026): the volunteers who can take it — Ready for the
 * program, never anyone still missing something (that is the Volunteers page's business) — the ones free at that day
 * and time first, then everyone else in the program. One can be picked (the office emails that one to confirm), or
 * none (the office emails everyone). Nothing is sent from here; the sheet's buttons do that.
 */
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { PickerView } from '@shared/volunteers-api';
import { AlmostLine } from './Ready171';

export function orderedVolunteers(p: PickerView | undefined): { free: { volunteerId: string; name: string }[]; rest: { volunteerId: string; name: string }[] } {
  if (!p) return { free: [], rest: [] };
  const freeIds = new Set([...p.first, ...p.available].map((v) => v.volunteerId));
  const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name);
  return { free: p.all.filter((v) => freeIds.has(v.volunteerId)).sort(byName), rest: p.all.filter((v) => !freeIds.has(v.volunteerId)).sort(byName) };
}

export function VolunteerChooser({
  pick,
  loading,
  selected,
  onSelect,
  program,
  service,
  allowPick = true,
  onClose,
  almostTestId = 'req-almost',
  marked,
}: {
  pick: PickerView | undefined;
  loading: boolean;
  selected: string | null;
  onSelect: (volunteerId: string | null) => void;
  program: string;
  service: string;
  /** false: the list is shown, nobody is picked (a group outing goes to everyone). */
  allowPick?: boolean;
  onClose?: () => void;
  /** Eli, 20 Oct 2026: the test id of the "signed up but not Ready" line under the list (staff screens only). */
  almostTestId?: string;
  /** D-1103-PAIR: volunteers who marked the chosen day and time (VOL_USUAL_PARTNER on). They show "Marked this time". */
  marked?: ReadonlySet<string>;
}) {
  const { t } = useTranslation();
  const id = useId();
  const ordered = orderedVolunteers(pick);
  const markedFirst = (l: typeof ordered.free) => (marked?.size ? [...l.filter((v) => marked.has(v.volunteerId)), ...l.filter((v) => !marked.has(v.volunteerId))] : l);
  const free = markedFirst(ordered.free);
  const rest = markedFirst(ordered.rest);
  if (loading && !pick) return <p className="help" role="status">{t('vb.loadingVols')}</p>;
  // D-1020-NOTREADY: who signed up but is not Ready, and what is missing — for the office to chase before sending; they are never sent this
  const almost = <AlmostLine almost={pick?.almost} service={null} testId={almostTestId} />;
  if (!free.length && !rest.length)
    return (
      <>
        <div className="warn" role="note" data-testid="vb-none">
          <div>
            <b>{t('vb.noneReady', { program })}</b>{' '}
            <Link className="btn secondary sm" to={`/volunteers?service=${service}`} onClick={onClose}>
              {t('vb.seeVolunteers')}
            </Link>
          </div>
        </div>
        {almost}
      </>
    );
  const row = (v: { volunteerId: string; name: string }, isFree: boolean) =>
    allowPick ? (
      <label key={v.volunteerId} className="vb-vol" data-testid={`vb-vol-${v.volunteerId}`}>
        <input type="radio" name={id} checked={selected === v.volunteerId} onChange={() => onSelect(v.volunteerId)} />
        <span>{v.name}</span>
        {isFree ? <span className="status conf">{t('vb.freeThen')}</span> : null}
        {marked?.has(v.volunteerId) ? <span className="status conf">{t('pair1103.marked')}</span> : null}
      </label>
    ) : (
      <li key={v.volunteerId} data-testid={`vb-vol-${v.volunteerId}`}>
        {v.name} {isFree ? <span className="status conf">{t('vb.freeThen')}</span> : null}{marked?.has(v.volunteerId) ? <> <span className="status conf">{t('pair1103.marked')}</span></> : null}
      </li>
    );
  const group = (title: string, list: typeof free, isFree: boolean, testId: string) =>
    list.length ? (
      <div data-testid={testId}>
        <p className="eyebrow">
          {title} · <span className="tnum">{list.length}</span>
        </p>
        {allowPick ? <div style={{ display: 'grid', gap: 6 }}>{list.map((v) => row(v, isFree))}</div> : <ul>{list.map((v) => row(v, isFree))}</ul>}
      </div>
    ) : null;
  return (
    <fieldset className="field" data-testid="vb-chooser">
      <legend>{allowPick ? t('vb.pickLegend', { program }) : t('vb.listLegend', { program })}</legend>
      {group(t('vb.groupFree'), free, true, 'vb-free')}
      {group(t('vb.groupRest', { program }), rest, false, 'vb-rest')}
      {allowPick && selected ? (
        <button type="button" className="btn ghost sm" onClick={() => onSelect(null)} data-testid="vb-clear">
          {t('vb.clearPick')}
        </button>
      ) : null}
      <p className="help">{allowPick ? t('vb.pickHelp') : t('vb.listHelp')}</p>
      {almost}
    </fieldset>
  );
}

/** A place typed by the office (not from the list) joins the approved places when the booking is saved (Eli, 17 Oct). */
export async function savePlace(post: (url: string, body: unknown) => Promise<unknown>, dest: { name: string; address: string; approved: boolean } | null, category: string): Promise<void> {
  if (!dest || dest.approved || !dest.name.trim() || !dest.address.trim()) return;
  try {
    await post('/api/volunteers/places', { category, name: dest.name.trim(), address: dest.address.trim(), phone: '', notes: '' });
  } catch {
    // already on the list, or the list is not reachable — the booking itself carries the place either way
  }
}
